import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { LuActivity as Activity, LuPause as Pause, LuPlay as Play, LuFlag as Flag, LuCrosshair as Crosshair } from 'react-icons/lu'
import SectionCard from './SectionCard'
import { themeColors, brand } from '../../../../theme'
import { parseUtcDate } from '../../../../lib/dates'
import { formatDuration } from '../../../../lib/trackAnalytics'
import { getShipmentState } from '../../../../lib/shipmentStates'
import type { ShipmentEventResponse, ShipmentPauseEventResponse } from '../../../../api/models/response/shipment'

const colors = themeColors

function dateTimeFromMs(ms: number): string {
  const d = new Date(ms)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

interface TimelineEntry {
  key: string
  time: number
  title: string
  subtitle?: string
  color: string
  icon: typeof Pause
  lat: number | null
  lng: number | null
}

interface Props {
  pauseEvents: ShipmentPauseEventResponse[]
  stateChanges: ShipmentEventResponse[]
  onFocus: (lat: number, lng: number) => void
  delay?: number
}

export default function ShipmentEventsTimeline({ pauseEvents, stateChanges, onFocus, delay = 0 }: Props) {
  const entries: TimelineEntry[] = []

  // Cambios de estado (cada uno con su color de estado).
  for (const sc of stateChanges) {
    const def = getShipmentState(sc.shipmentState)
    entries.push({
      key: `state-${sc.id}`,
      time: parseUtcDate(sc.changedAt).getTime(),
      title: def.label,
      subtitle: sc.changedByUserName ? `Por ${sc.changedByUserName}` : undefined,
      color: def.color,
      icon: Flag,
      lat: sc.latitude,
      lng: sc.longitude,
    })
  }

  // Pausas y reanudaciones (con motivo y duración hasta el reanudar siguiente).
  for (let i = 0; i < pauseEvents.length; i++) {
    const e = pauseEvents[i]
    const time = parseUtcDate(e.occurredAt).getTime()
    if (e.actionType === 'pausar') {
      const resume = pauseEvents.slice(i + 1).find((r) => r.actionType === 'reanudar')
      let durationLabel = 'en pausa'
      if (resume) {
        const ms = parseUtcDate(resume.occurredAt).getTime() - time
        if (ms > 0) durationLabel = formatDuration(ms)
      }
      entries.push({
        key: `pause-${e.id}`,
        time,
        title: 'Pausa',
        subtitle: `${e.reasonText || e.reasonName || 'Sin motivo'} · ${durationLabel}`,
        color: '#d49a2a',
        icon: Pause,
        lat: e.latitude,
        lng: e.longitude,
      })
    } else {
      entries.push({
        key: `resume-${e.id}`,
        time,
        title: 'Reanudación',
        subtitle: 'Seguimiento reanudado',
        color: '#1d6664',
        icon: Play,
        lat: e.latitude,
        lng: e.longitude,
      })
    }
  }

  entries.sort((a, b) => a.time - b.time)

  const pauseCount = pauseEvents.filter((e) => e.actionType === 'pausar').length

  return (
    <SectionCard
      title="Eventos del traslado"
      subtitle={
        entries.length === 0
          ? 'Sin eventos'
          : `${entries.length} evento${entries.length > 1 ? 's' : ''}${pauseCount > 0 ? ` · ${pauseCount} pausa${pauseCount > 1 ? 's' : ''}` : ''}`
      }
      icon={Activity}
      accent={brand.green}
      delay={delay}
    >
      {entries.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={8} gap={2}>
          <Activity size={28} color={colors.textDimmed} />
          <Text fontSize="sm" color={colors.textMuted} textAlign="center">
            Sin eventos de estado ni pausas registrados
          </Text>
        </Flex>
      ) : (
        <VStack gap={2} alignItems="stretch" maxH="360px" overflowY="auto">
          {entries.map((entry) => {
            const Icon = entry.icon
            const hasCoords = entry.lat != null && entry.lng != null
            return (
              <Flex
                key={entry.key}
                alignItems="center"
                gap={3}
                p={3}
                borderRadius="xl"
                border="1px solid"
                borderColor={colors.border}
                bg={colors.bgPanel}
                _hover={hasCoords ? { borderColor: brand.green } : undefined}
                transition="all 0.15s"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg={`${entry.color}1F`}
                  color={entry.color}
                  flexShrink={0}
                >
                  <Icon size={15} />
                </Flex>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="700" color={colors.text} truncate>
                    {entry.title}
                  </Text>
                  <Text fontSize="11px" color={colors.textMuted} truncate>
                    {dateTimeFromMs(entry.time)}
                    {entry.subtitle ? ` · ${entry.subtitle}` : ''}
                  </Text>
                </Box>
                {hasCoords ? (
                  <Box
                    as="button"
                    p={2}
                    borderRadius="lg"
                    cursor="pointer"
                    color={colors.textMuted}
                    _hover={{ bg: colors.bgHover, color: brand.green }}
                    transition="all 0.15s"
                    title="Centrar en el mapa"
                    onClick={() => onFocus(entry.lat as number, entry.lng as number)}
                  >
                    <Crosshair size={16} />
                  </Box>
                ) : (
                  <Text fontSize="9px" color={colors.textDimmed} flexShrink={0} title="Este evento no registró coordenadas">
                    sin ubicación
                  </Text>
                )}
              </Flex>
            )
          })}
        </VStack>
      )}
    </SectionCard>
  )
}
